import { Copy } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useState } from "react";
import { object, string } from "yup";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldError, FieldLabel, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor,
} from "@/components/ui/combobox";

import { INDUSTRIES } from "components/user/sign-in/states";
import { GetUserIfLoggedIn, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { Organisation } from "data/models";
import { editOrganisation } from "data/mutations-common";
import { resolveIdentityId } from "utils/identity";

const ORG_SIZES = [
  { value: "1-10 employees", label: "1-10 employees (Micro business/Startup)" },
  { value: "11-50 employees", label: "11-50 employees (Small business)" },
  { value: "51-200 employees", label: "51-200 employees (Mid-sized business)" },
  { value: "201-500 employees", label: "201-500 employees (Large mid-market)" },
  {
    value: "501-1,000 employees",
    label: "501-1,000 employees (Small enterprise)",
  },
  {
    value: "1,001-5,000 employees",
    label: "1,001-5,000 employees (Mid enterprise)",
  },
  {
    value: "5,001-10,000 employees",
    label: "5,001-10,000 employees (Large enterprise)",
  },
  {
    value: "10,000+ employees",
    label: "10,000+ employees (Global enterprise)",
  },
];

const validationSchema = object({
  name: string().required("Organisation name is required"),
  abn: string()
    .matches(/^\d+$/, "ABN must be 11 digits")
    .min(11, "ABN must be 11 digits")
    .max(11, "ABN must be 11 digits")
    .nullable(true),
  industry: string()
    .oneOf([null, ...INDUSTRIES])
    .nullable(true),
  id: string().nullable(true),
});

interface OrganisationProfileCardProps {
  organisation: Organisation;
}

export const OrganisationProfileCard = ({
  organisation,
}: OrganisationProfileCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const industryAnchorRef = useComboboxAnchor();

  const [, setIdentity] = useIdentity();
  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const queryClient = useQueryClient();

  const organisationMutation = useMutation({
    mutationFn: (org: Organisation) => editOrganisation(org),
    onSuccess: (updatedOrganisation) => {
      queryClient.setQueryData(
        [QueryKeys.organisation, identityId],
        updatedOrganisation,
      );
      setIdentity((prevIdentity) => {
        const user = GetUserIfLoggedIn(prevIdentity);
        if (user) {
          return {
            ...user,
            organisations: user.organisations.map((org) =>
              org.active ? { ...org, name: updatedOrganisation.name } : org,
            ),
          };
        }
        return prevIdentity;
      });
      setIsEditing(false);
    },
  });

  return (
    <Formik<Organisation>
      enableReinitialize={true}
      initialValues={{
        name: organisation.name,
        id: organisation.id,
        email: organisation.email,
        address: organisation.address,
        industry: organisation.industry,
        phoneNumber: organisation.phoneNumber,
        abn: organisation.abn,
        orgSize: organisation.orgSize,
        hasUsedTrial: organisation.hasUsedTrial,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        organisationMutation.mutate({ ...values, id: organisation.id });
      }}
    >
      {({
        values,
        handleChange,
        setFieldValue,
        handleBlur,
        errors,
        touched,
        resetForm,
        isValid,
        dirty,
      }: any) => (
        <Form>
          <Card>
            <CardHeader>
              <CardTitle>Organisation Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <FieldSet>
                <Field>
                  <FieldLabel htmlFor="name">Organisation Name</FieldLabel>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name ?? ""}
                    maxLength={120}
                    disabled={!isEditing}
                    aria-invalid={touched.name && Boolean(errors.name)}
                  />
                  {touched.name && errors.name && (
                    <FieldError>{errors.name}</FieldError>
                  )}
                </Field>
                <Field>
                  <FieldLabel htmlFor="organisation-id">
                    Organisation ID
                  </FieldLabel>
                  <div className="flex items-center gap-2">
                    <p id="organisation-id" className="text-base">
                      {values.id}
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-lg"
                      onClick={() =>
                        navigator.clipboard.writeText(values.id ?? "")
                      }
                    >
                      <Copy />
                    </Button>
                  </div>
                </Field>
                <Field>
                  <FieldLabel htmlFor="industry">Industry</FieldLabel>
                  <div ref={industryAnchorRef}>
                    <Combobox
                      value={values.industry ?? ""}
                      onValueChange={(v) =>
                        setFieldValue("industry", (v as string) ?? "")
                      }
                      inputValue={values.industry ?? ""}
                      onInputValueChange={(v) =>
                        setFieldValue("industry", v ?? "")
                      }
                    >
                      <ComboboxInput
                        placeholder="Select industry"
                        disabled={!isEditing}
                      />
                      <ComboboxContent anchor={industryAnchorRef}>
                        <ComboboxList>
                          {INDUSTRIES.map((option) => (
                            <ComboboxItem key={option} value={option}>
                              {option}
                            </ComboboxItem>
                          ))}
                          <ComboboxEmpty>No results found.</ComboboxEmpty>
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                  </div>
                  {touched.industry && errors.industry && (
                    <FieldError>{errors.industry}</FieldError>
                  )}
                </Field>
                <Field>
                  <FieldLabel>Organisation Size</FieldLabel>
                  <Select
                    value={values.orgSize ?? ""}
                    onValueChange={(value) => setFieldValue("orgSize", value)}
                    disabled={!isEditing}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select organisation size" />
                    </SelectTrigger>
                    <SelectContent>
                      {ORG_SIZES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field>
                  <FieldLabel htmlFor="abn">ABN</FieldLabel>
                  <Input
                    id="abn"
                    name="abn"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.abn ?? ""}
                    maxLength={11}
                    disabled={!isEditing}
                    aria-invalid={touched.abn && Boolean(errors.abn)}
                  />
                  {touched.abn && errors.abn && (
                    <FieldError>{errors.abn}</FieldError>
                  )}
                </Field>
              </FieldSet>
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              {!isEditing && (
                <Button type="button" onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              )}
              {isEditing && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={organisationMutation.isPending}
                    onClick={() => {
                      resetForm();
                      setIsEditing(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      !isValid || !dirty || organisationMutation.isPending
                    }
                  >
                    {organisationMutation.isPending ? (
                      <>
                        <Spinner />
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </>
              )}
            </CardFooter>
          </Card>
        </Form>
      )}
    </Formik>
  );
};
